import { useState } from 'react';
import type { WeeklySchedule } from '@acs/shared';
import { useMediaQuery } from '../../shared/lib/useMediaQuery.js';
import { PHONE_QUERY, TOUCH_QUERY } from '../../shared/lib/breakpoints.js';
import { BlockPopover } from './BlockPopover.js';
import { ListScheduleEditor } from './ListScheduleEditor.js';
import { TimeGrid, type TimeGridBlock, type TimeGridColumn } from './TimeGrid.js';
import {
  clampBlock,
  daysToWeekly,
  normalizeDay,
  visibleWindow,
  weeklyToDays,
  type GridInterval,
} from './gridMath.js';
import { weekdayNames } from './weekDates.js';
import { useI18n } from '../../i18n/index.js';

type View = 'grid' | 'list';
type FormFactor = 'phone' | 'wide';

/**
 * The owner's explicit choice on a WIDE screen is remembered, so a laptop that
 * prefers the list keeps it. On a mobile device the list is where every visit
 * starts: the grid is one tap away for this visit only, because a remembered
 * grid pick made the phone open on an unusable seven-column grid with no way
 * to tell why. The old shared key (`tc24.scheduleView`) and the old phone key
 * are deliberately not read any more.
 */
const WIDE_VIEW_KEY = 'tc24.scheduleView.wide';
/** On a phone or tablet a seven-column drag grid leaves each day a sliver too
 *  narrow to grab with a finger, so the list is the default there and the grid
 *  stays one tap away. */
const DEFAULT_VIEW: Record<FormFactor, View> = { phone: 'list', wide: 'grid' };

/**
 * The shared weekly-hours editor (doc 10 §4.1–§4.3, §5.1): a Google-Calendar
 * week grid with an accessible list-view twin, used by both the staff weekly
 * tab and the business "hours (fallback)" card. Controlled — the parent owns
 * the dirty flag and Save (the whole doc is written at once, doc 10 §5.5).
 */
export function WeeklyScheduleEditor({
  value,
  onChange,
}: {
  value: WeeklySchedule['hours'];
  onChange(next: WeeklySchedule['hours']): void;
}) {
  const { t, tag } = useI18n();
  const narrow = useMediaQuery(PHONE_QUERY);
  const touch = useMediaQuery(TOUCH_QUERY);
  const formFactor: FormFactor = narrow || touch ? 'phone' : 'wide';
  // Kept per form factor, so crossing the breakpoint (a resized window)
  // switches to that screen's own view instead of carrying the other across.
  const [chosen, setChosen] = useState<Partial<Record<FormFactor, View>>>(() => ({ wide: readWideView() }));
  const view = chosen[formFactor] ?? DEFAULT_VIEW[formFactor];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const days = weeklyToDays(value);
  const columns: TimeGridColumn[] = weekdayNames(tag, 'short').map((label, i) => ({ id: String(i), label }));

  const blocks: TimeGridBlock[] = days.flatMap((intervals, i) =>
    intervals.map((iv) => ({ id: `${i}#${iv.startMin}`, columnId: String(i), kind: 'editable' as const, ...iv })),
  );

  const blocksToDays = (bs: TimeGridBlock[]): GridInterval[][] =>
    columns.map((_, i) =>
      normalizeDay(bs.filter((b) => Number(b.columnId) === i).map((b) => ({ startMin: b.startMin, endMin: b.endMin }))),
    );

  const commitBlocks = (next: TimeGridBlock[]) => onChange(daysToWeekly(blocksToDays(next)));
  const setDays = (next: GridInterval[][]) => onChange(daysToWeekly(next));
  const chooseView = (v: View) => {
    setChosen((c) => ({ ...c, [formFactor]: v }));
    if (formFactor === 'wide') {
      try { localStorage.setItem(WIDE_VIEW_KEY, v); } catch { /* ignore */ }
    }
  };

  const selected = selectedId ? blocks.find((b) => b.id === selectedId) ?? null : null;
  const win = visibleWindow(days);

  return (
    <div className="schedule-editor">
      <div className="schedule-editor-toolbar">
        <div className="segmented" role="tablist" aria-label={t('grid.editorView')}>
          <button type="button" role="tab" aria-selected={view === 'grid'} className={view === 'grid' ? 'is-active' : ''} onClick={() => chooseView('grid')}>
            {t('grid.view.grid')}
          </button>
          <button type="button" role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'is-active' : ''} onClick={() => chooseView('list')}>
            {t('grid.view.list')}
          </button>
        </div>
      </div>

      {view === 'grid' ? (
        <div className="schedule-grid-wrap">
          <TimeGrid
            columns={columns}
            blocks={blocks}
            window={win}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCommit={commitBlocks}
            onKeyCommand={(id, cmd) => {
              if (cmd === 'delete') {
                setDays(blocksToDays(blocks.filter((b) => b.id !== id)));
                setSelectedId(null);
              } else if (cmd === 'open') {
                setSelectedId(id);
              }
            }}
          />
          {selected && (
            <BlockPopover
              interval={{ startMin: selected.startMin, endMin: selected.endMin }}
              copyTargets={columns.filter((c) => c.id !== selected.columnId).map((c) => ({ id: c.id, label: String(c.label) }))}
              onChange={(iv) => {
                const clamped = clampBlock(iv);
                setDays(blocksToDays(blocks.map((b) => (b.id === selected.id ? { ...b, ...clamped } : b))));
                setSelectedId(`${selected.columnId}#${clamped.startMin}`);
              }}
              onCopyTo={(targetId) =>
                setDays(blocksToDays([...blocks, { id: 'copy', columnId: targetId, kind: 'editable', startMin: selected.startMin, endMin: selected.endMin }]))
              }
              onDelete={() => {
                setDays(blocksToDays(blocks.filter((b) => b.id !== selected.id)));
                setSelectedId(null);
              }}
              onClose={() => setSelectedId(null)}
            />
          )}
          <p className="field-hint" style={{ marginTop: 8 }}>{t('grid.dragHint')}</p>
        </div>
      ) : (
        <ListScheduleEditor days={days} onChange={setDays} />
      )}
    </div>
  );
}

/** The view the owner last picked on a wide screen, or undefined when they
 *  never have — which is what lets the default follow the screen. */
function readWideView(): View | undefined {
  try {
    const v = localStorage.getItem(WIDE_VIEW_KEY);
    return v === 'list' || v === 'grid' ? v : undefined;
  } catch {
    return undefined;
  }
}
